/**
 * The first night's wiring on home (v1 ticket 40), DOM-free.
 *
 * - `homeOpens`: what home does with a `GET /api/town` reply. The opening while it is due (a new account with no
 *   moment), then a waiting landing (41's, which come before naming), then the naming sheet over the town (a learner
 *   from before the first night, or a new one who left during the map), else the town (or, with no moment, the empty
 *   land). The opening and the naming sheet show once a visit, so an ack that never got through never loops.
 * - The held layers: the opening and its map stay on screen, fully visible, while home draws its land under them.
 *   Home calls `releaseFirstNight()` after the land's first frame: the map lifts and the opening fades at once, and
 *   each is removed when its own animation ends, never on a timer. Any other route releases them too.
 * - `ackTownSeen`: acking a part seen (`POST /api/town/seen`) is best-effort, but retries a lost connection, a 5xx,
 *   408 or 429 a few times, so a tester on a flaky link (Iran included) doesn't get the opening or the naming sheet
 *   back next visit just because the ack didn't land.
 */
import { api, isRetryable } from './api';
import { nextLanding } from './town/landing';
import type { OwnTown } from './town/own-town';

export type HomeOpens = 'opening' | 'landing' | 'naming' | 'town';

/** Parts of the first night shown in this visit (in memory: a reload asks the server again). */
const shown = new Set<'opening' | 'naming'>();

/** The opening or the naming sheet has been shown in this visit: home does not open it again until a reload. */
export function markShown(part: 'opening' | 'naming'): void {
  shown.add(part);
}

/** Forgets what this visit has shown (tests). */
export function resetShown(): void {
  shown.clear();
}

/** What home opens for this town: the opening, a waiting landing, the naming sheet, or the town itself. */
export function homeOpens(own: Pick<OwnTown, 'districts' | 'landing' | 'due'>): HomeOpens {
  if (own.due?.opening && !shown.has('opening')) return 'opening';
  if (nextLanding(own as OwnTown)) return 'landing';
  if (own.due?.naming && !shown.has('naming')) return 'naming';
  return 'town';
}

/** The land is empty: no moment yet, so home shows the named land waiting for the first recording. */
export function isEmptyLand(own: Pick<OwnTown, 'districts'>): boolean {
  return own.districts.length === 0;
}

/** A layer left on screen for home to take away: `go` animates it out and resolves at its end. */
export interface HeldLayer {
  go(): Promise<void>;
  remove(): void;
}

let held: HeldLayer[] = [];

/** The opening's layers stay on screen while home draws under them. */
export function holdFirstNight(...layers: HeldLayer[]): void {
  held.push(...layers);
}

/** Whether layers are held (home waits for its first frame before releasing them). */
export function firstNightHeld(): boolean {
  return held.length > 0;
}

/** Takes the held layers away together, each removed when its own animation has ended; resolves when all are gone. */
export function releaseFirstNight(): Promise<void> {
  const going = held;
  held = [];
  return Promise.all(going.map((l) => l.go().catch(() => {}).then(() => l.remove()))).then(() => {});
}

const SEEN_RETRY_DELAYS_MS = [1000, 3000, 9000];

/**
 * Acks a first-night part seen. Settles after the first attempt (never blocks a caller waiting on it before
 * navigating), but a network error, a 5xx, 408 or 429 keeps retrying a few times in the background with backoff;
 * a 4xx refusal never retries.
 */
export function ackTownSeen(part: 'opening' | 'naming'): Promise<void> {
  const retry = (n: number): void => {
    if (n >= SEEN_RETRY_DELAYS_MS.length) return;
    setTimeout(() => {
      api.townSeen(part).catch((e) => { if (isRetryable(e)) retry(n + 1); });
    }, SEEN_RETRY_DELAYS_MS[n]);
  };
  return api.townSeen(part).then(
    () => {},
    (e) => { if (isRetryable(e)) retry(0); },
  );
}
